import type { MetricColorRole } from './types'

/**
 * The single colour authority for the chart components. Colour is never a prop:
 * the {@link ChartSeriesProvider} resolves each series through here, so the line,
 * tooltip, legend, and (later) breakdown bars all read the same values.
 */

/** Max number of distinct channel lines a chart draws (caps the time-series fetch). */
export const MAX_CHART_CHANNELS = 10

/**
 * Categorical palette for positionally-coloured series (All-Channels channels).
 * Ten distinct hues assigned by index, wrapping past the tenth. Order and colours
 * follow the Insights "Color charts" spec; each `--chart-series-*` token is
 * defined (light + dark) in the chart CSS, so teal and gray get their
 * per-theme accessibility steps automatically.
 */
const CHANNEL_PALETTE: readonly [string, ...string[]] = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--chart-series-4)',
  'var(--chart-series-5)',
  'var(--chart-series-6)',
  'var(--chart-series-7)',
  'var(--chart-series-8)',
  'var(--chart-series-9)',
  'var(--chart-series-10)',
]

/** Semantic metric roles → brand colour tokens (single-metric charts). The
 *  `--chart-color-*` tokens are defined in the chart CSS (light + dark). */
const METRIC_COLORS: Record<MetricColorRole, string> = {
  followers: 'var(--chart-color-followers)',
  'followers-net-new': 'var(--chart-color-followers-net-new)',
  reach: 'var(--chart-color-reach)',
  views: 'var(--chart-color-views)',
  posts: 'var(--chart-color-posts)',
  'benchmark-channel': 'var(--chart-color-benchmark-channel)',
  'benchmark-median': 'var(--chart-color-benchmark-median)',
}

/** Colour of the interpolated (computed/missing) no-data line segments — a subtle
 *  neutral grey, darker than the grid so it reads as "no data" but stays legible. */
export const NO_DATA_COLOR = 'var(--color-border-neutral)'

/** Fill for a hollow no-data marker: the chart surface the card sits on, so the
 *  marker reads as an open ring (surface fill + grey {@link NO_DATA_COLOR} stroke). */
export const SURFACE_COLOR = 'var(--color-bg-surface-raised)'

/** Positional channel colour, wrapping past the palette length. */
export function getChannelColor(index: number): string {
  return CHANNEL_PALETTE[index % CHANNEL_PALETTE.length]
}

/** Semantic metric colour token. */
export function getMetricColor(role: MetricColorRole): string {
  return METRIC_COLORS[role]
}
